import { useQuery } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import {
  getEdition,
  getEditions,
  getLatest,
  getPulseProfiles,
  getWeeklyFeatures,
} from "@/api/pulse";
import { queryKeys } from "@/api/queryKeys";
import type { EditionSummary, PulseEdition } from "@/types";

export function usePulseProfiles(profileSlug?: string) {
  return useQuery({
    queryKey: queryKeys.pulse.profiles(profileSlug),
    queryFn: () => getPulseProfiles(profileSlug),
  });
}

export function useLatest(profileSlug?: string) {
  return useQuery({
    queryKey: queryKeys.pulse.latest(profileSlug),
    queryFn: () => getLatest({ profileSlug }),
  });
}

export function useEditions(profileSlug?: string) {
  return useQuery({
    queryKey: queryKeys.pulse.editions(profileSlug),
    queryFn: () => getEditions({ profileSlug }),
  });
}

export function useEdition(fecha: string | undefined, profileSlug?: string) {
  return useQuery({
    queryKey: queryKeys.pulse.edition(fecha ?? "", profileSlug),
    queryFn: () => getEdition(fecha as string, { profileSlug }),
    enabled: Boolean(fecha),
  });
}

export function useWeeklyFeatures(limit = 3, profileSlug?: string) {
  return useQuery({
    queryKey: queryKeys.pulse.weeklyFeatures(limit, profileSlug),
    queryFn: () => getWeeklyFeatures(limit, { profileSlug }),
  });
}

function is404(error: unknown): boolean {
  if (error instanceof ApiError) return error.status === 404;
  return (error as { status?: number })?.status === 404;
}

function editionUsable(edition: PulseEdition | undefined): edition is PulseEdition {
  return !!edition && edition.card_count > 0;
}

export interface ResolvedEditionState {
  edition: PulseEdition | undefined;
  editions: EditionSummary[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isFallback: boolean;
  requestedDate: string | undefined;
  resolvedDate: string | undefined;
  isEmpty: boolean;
}

/** Resuelve la edición primaria o hace fallback a la última publicada con cards. */
export function useResolvedEdition(fecha?: string, profileSlug?: string): ResolvedEditionState {
  const editionsQuery = useEditions(profileSlug);
  const latestQuery = useLatest(profileSlug);
  const editionQuery = useEdition(fecha, profileSlug);

  const primaryQuery = fecha ? editionQuery : latestQuery;
  const editions = editionsQuery.data?.editions ?? [];

  const primaryUsable = editionUsable(primaryQuery.data);
  const primaryDone = !primaryQuery.isLoading;
  const needsFallback = primaryDone && !primaryUsable;

  const requestedDate = fecha ?? primaryQuery.data?.fecha;
  const fallbackFecha = editions.find((e) => e.card_count > 0)?.fecha;
  const fallbackEnabled =
    needsFallback && Boolean(fallbackFecha) && fallbackFecha !== requestedDate;

  const fallbackQuery = useEdition(fallbackEnabled ? fallbackFecha : undefined, profileSlug);

  const fallbackUsable = editionUsable(fallbackQuery.data);
  const edition = fallbackUsable
    ? fallbackQuery.data
    : primaryUsable
      ? primaryQuery.data
      : undefined;

  const isLoading =
    primaryQuery.isLoading ||
    editionsQuery.isLoading ||
    (fallbackEnabled && fallbackQuery.isLoading && !edition);

  const hasNoPublishedEditions =
    editionsQuery.isSuccess && editions.length === 0 && primaryDone;

  const fallbackFailed =
    fallbackEnabled && fallbackQuery.isError && !fallbackQuery.isLoading;
  const primaryFailedNon404 =
    primaryQuery.isError && !is404(primaryQuery.error);
  const noFallbackAvailable = needsFallback && !fallbackEnabled && primaryDone;

  const isEmpty =
    !isLoading &&
    !edition &&
    !primaryFailedNon404 &&
    (hasNoPublishedEditions ||
      (noFallbackAvailable && (is404(primaryQuery.error) || !primaryUsable)));

  const isError =
    !edition && !isLoading && !isEmpty && (fallbackFailed || primaryFailedNon404);

  const error = fallbackFailed
    ? fallbackQuery.error
    : primaryFailedNon404
      ? primaryQuery.error
      : null;

  return {
    edition,
    editions,
    isLoading,
    isError,
    error,
    isFallback: fallbackUsable,
    requestedDate,
    resolvedDate: edition?.fecha,
    isEmpty,
  };
}
